// ============================================
// Creator Module - Intro Blocks (ES6)
// ============================================

import { showToast, showGlobalLoading, hideGlobalLoading, escapeHtml } from '../utils.js';
import { getCurrentUser } from '../app/state.js';
import {
  getIntroBlocks, setIntroBlocks,
  getCurrentIntroType, setCurrentIntroType,
  getSelectedIntroBlockIndex, setSelectedIntroBlockIndex,
  getIntroImagePage, setIntroImagePage,
  INTRO_IMAGE_PAGE_SIZE,
  getIntroImageLoading, setIntroImageLoading,
  getIntroImageHasMore, setIntroImageHasMore,
  getIntroImageMode, setIntroImageMode,
  getEditingIntroImageIndex, setEditingIntroImageIndex,
  getCurrentEditorData,
  setCache
} from './state.js';
import { getMainImageUrl, updatePublishButtonState } from './editor.js';
import { updateTabCheckStatus } from './editor/events.js';
import { loadGeneratedImagesFromStorage } from './image-gallery.js';

// 편집 중인 블록 인덱스 (원래 위치 유지용)
let editingIntroBlockIndex = null;

// ============================================
// 인트로 블록 렌더링
// ============================================

export function renderIntroPreview() {
  const container = document.getElementById('introChatPreview');
  if (!container) return;

  const introBlocks = getIntroBlocks();

  if (introBlocks.length === 0) {
    container.innerHTML = `
      <div class="intro-chat-empty">
        <p>아래에서 메시지를 추가해주세요</p>
      </div>
    `;
    setSelectedIntroBlockIndex(null);
    return;
  }

  const charName = document.getElementById('editorCharName')?.value?.trim() || '점술가';
  const charImage = getMainImageUrl() || '';
  const totalBlocks = introBlocks.length;

  const getBlockActions = (idx, isImage = false) => `
    <div class="intro-block-actions-overlay">
      ${isImage ? `
        <button class="intro-action-btn" data-action="editIntroImage" data-index="${idx}" title="이미지 변경">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
        </button>
      ` : `
        <button class="intro-action-btn" data-action="editIntroBlock" data-index="${idx}" title="수정">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
        </button>
      `}
      ${idx > 0 ? `
        <button class="intro-action-btn" data-action="moveIntroBlockUp" data-index="${idx}" title="위로 이동">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="18 15 12 9 6 15"/>
          </svg>
        </button>
      ` : ''}
      ${idx < totalBlocks - 1 ? `
        <button class="intro-action-btn" data-action="moveIntroBlockDown" data-index="${idx}" title="아래로 이동">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
      ` : ''}
      <button class="intro-action-btn delete" data-action="deleteIntroBlock" data-index="${idx}" title="삭제">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
  `;

  container.innerHTML = introBlocks.map((block, idx) => {
    const isSelected = getSelectedIntroBlockIndex() === idx ? 'selected' : '';

    if (block.type === 'narration') {
      return `
        <div class="intro-block-wrapper ${isSelected}" data-index="${idx}">
          ${getBlockActions(idx)}
          <div class="narration-block">${formatUserMarker(block.content)}</div>
        </div>
      `;
    } else if (block.type === 'dialogue') {
      return `
        <div class="intro-block-wrapper ${isSelected}" data-index="${idx}">
          ${getBlockActions(idx)}
          <div class="dialogue-block">
            <div class="dialogue-avatar">
              ${charImage ? `<img src="${charImage}" alt="${charName}" class="dialogue-avatar-img">` : '👤'}
            </div>
            <div class="dialogue-content">
              <div class="dialogue-speaker">${escapeHtml(charName)}</div>
              <div class="dialogue-bubble">
                <div class="dialogue-text">${formatUserMarker(block.content)}</div>
              </div>
            </div>
          </div>
        </div>
      `;
    } else if (block.type === 'thought') {
      return `
        <div class="intro-block-wrapper ${isSelected}" data-index="${idx}">
          ${getBlockActions(idx)}
          <div class="thought-block">
            <div class="thought-avatar">
              ${charImage ? `<img src="${charImage}" alt="${charName}" class="dialogue-avatar-img">` : '💭'}
            </div>
            <div class="thought-content">
              <div class="thought-speaker">${escapeHtml(charName)}</div>
              <div class="thought-bubble">
                <div class="thought-text">${formatUserMarker(block.content)}</div>
              </div>
            </div>
          </div>
        </div>
      `;
    } else if (block.type === 'user') {
      return `
        <div class="intro-block-wrapper user-block-wrapper ${isSelected}" data-index="${idx}">
          ${getBlockActions(idx)}
          <div class="message user">
            <div class="message-content">${formatUserMarker(block.content)}</div>
          </div>
        </div>
      `;
    } else if (block.type === 'image') {
      return `
        <div class="intro-block-wrapper intro-image-wrapper ${isSelected}" data-index="${idx}">
          ${getBlockActions(idx, true)}
          <div class="intro-image-block">
            <img src="${block.content}" alt="인트로 이미지">
          </div>
        </div>
      `;
    }
    return '';
  }).join('');

  // 이벤트 위임 설정
  container.onclick = (e) => {
    const actionEl = e.target.closest('[data-action]');
    if (actionEl) {
      e.stopPropagation();
      const idx = parseInt(actionEl.dataset.index, 10);
      const action = actionEl.dataset.action;

      switch (action) {
        case 'editIntroImage':
          editIntroImage(idx);
          break;
        case 'editIntroBlock':
          editIntroBlock(idx);
          break;
        case 'moveIntroBlockUp':
          moveIntroBlock(idx, -1);
          break;
        case 'moveIntroBlockDown':
          moveIntroBlock(idx, 1);
          break;
        case 'deleteIntroBlock':
          deleteIntroBlock(idx);
          break;
      }
      return;
    }

    // 블록 선택
    const wrapper = e.target.closest('.intro-block-wrapper');
    if (wrapper) {
      const idx = parseInt(wrapper.dataset.index, 10);
      selectIntroBlock(idx);
    } else {
      // 빈 공간 클릭 시 선택 해제
      deselectIntroBlock();
    }
  };
}

function formatUserMarker(text) {
  let escaped = escapeHtml(text);
  escaped = escaped.replace(/\{\{user\}\}/gi, '<span class="user-marker">{{user}}</span>');
  escaped = escaped.replace(/\*\*([^*]+)\*\*/g, '<strong class="bold-text">$1</strong>');
  escaped = escaped.replace(/\*([^*]+)\*/g, '<span class="action-text">$1</span>');
  return escaped;
}

// ============================================
// 인트로 타입 관리
// ============================================

export function setIntroType(type) {
  setCurrentIntroType(type);
  updateIntroTypeButtons();
}

export function updateIntroTypeButtons() {
  document.querySelectorAll('.intro-type-tab').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.type === getCurrentIntroType());
  });
}

// ============================================
// 인트로 블록 추가/편집/삭제
// ============================================

export function selectIntroBlock(idx) {
  setSelectedIntroBlockIndex(idx);
  document.querySelectorAll('#introChatPreview .intro-block-wrapper').forEach(el => {
    el.classList.remove('selected');
  });
  const selectedEl = document.querySelector(`#introChatPreview .intro-block-wrapper[data-index="${idx}"]`);
  if (selectedEl) {
    selectedEl.classList.add('selected');
  }
}

export function deselectIntroBlock() {
  setSelectedIntroBlockIndex(null);
  document.querySelectorAll('#introChatPreview .intro-block-wrapper').forEach(el => {
    el.classList.remove('selected');
  });
}

export function addIntroBlock() {
  const input = document.getElementById('introComposerInput');
  if (!input) return;

  const content = input.value.trim();
  if (!content) {
    showToast('내용을 입력해주세요');
    return;
  }

  const newBlock = {
    type: getCurrentIntroType(),
    content
  };

  // 편집 중이면 원래 위치에 삽입
  if (editingIntroBlockIndex !== null) {
    getIntroBlocks().splice(editingIntroBlockIndex, 0, newBlock);
    editingIntroBlockIndex = null;
    // 버튼 스타일 복원
    const addBtn = document.querySelector('#editorTabIntro .intro-add-btn');
    if (addBtn) addBtn.classList.remove('editing');
  } else {
    getIntroBlocks().push(newBlock);
  }

  input.value = '';
  input.style.height = 'auto';
  renderIntroPreview();
  updatePublishButtonState();
  updateTabCheckStatus();

  const container = document.getElementById('introChatPreview');
  if (container && editingIntroBlockIndex === null) {
    container.scrollTop = container.scrollHeight;
  }
}

export function editIntroBlock(index) {
  const introBlocks = getIntroBlocks();
  if (index < 0 || index >= introBlocks.length) return;

  const block = introBlocks[index];
  const input = document.getElementById('introComposerInput');
  if (input) {
    input.value = block.content;
    setCurrentIntroType(block.type);
    updateIntroTypeButtons();
    input.focus();
    input.style.height = 'auto';
    input.style.height = Math.min(input.scrollHeight, 100) + 'px';
  }

  // 편집 위치 저장 후 삭제
  editingIntroBlockIndex = index;
  introBlocks.splice(index, 1);

  // 버튼 스타일 변경
  const addBtn = document.querySelector('#editorTabIntro .intro-add-btn');
  if (addBtn) addBtn.classList.add('editing');

  renderIntroPreview();
  updateTabCheckStatus();
  deselectIntroBlock();
}

export function moveIntroBlock(index, direction) {
  const introBlocks = getIntroBlocks();
  const newIndex = index + direction;
  if (newIndex < 0 || newIndex >= introBlocks.length) return;

  const temp = introBlocks[index];
  introBlocks[index] = introBlocks[newIndex];
  introBlocks[newIndex] = temp;

  // 선택 상태를 새 인덱스로 업데이트
  setSelectedIntroBlockIndex(newIndex);

  renderIntroPreview();
  updatePublishButtonState();
  updateTabCheckStatus();
}

export function deleteIntroBlock(index) {
  const introBlocks = getIntroBlocks();
  if (index < 0 || index >= introBlocks.length) return;
  introBlocks.splice(index, 1);
  renderIntroPreview();
  updatePublishButtonState();
  updateTabCheckStatus();
}

// ============================================
// 인트로 이미지 관리
// ============================================

export function addIntroImage() {
  openIntroImageModal();
}

async function openIntroImageModal() {
  setIntroImageMode('insert');
  await openIntroImageBottomSheet('이미지 선택');
}

export function editIntroImage(blockIndex) {
  setEditingIntroImageIndex(blockIndex);
  openIntroImageModalForEdit();
}

async function openIntroImageModalForEdit() {
  setIntroImageMode('replace');
  await openIntroImageBottomSheet('이미지 변경');
}

async function openIntroImageBottomSheet(title) {
  const currentEditorData = getCurrentEditorData();
  const characterImages = currentEditorData.images || [];
  const generatedImages = currentEditorData.generatedImages || [];

  if (getCurrentUser() && generatedImages.length === 0) {
    showGlobalLoading('이미지 불러오는 중...');
    try {
      await loadGeneratedImagesFromStorage();
    } finally {
      hideGlobalLoading();
    }
  }

  const allGeneratedImages = currentEditorData.generatedImages || [];

  if (characterImages.length === 0 && allGeneratedImages.length === 0) {
    showToast('사용 가능한 이미지가 없습니다');
    if (getIntroImageMode() === 'replace') setEditingIntroImageIndex(null);
    return;
  }

  setIntroImagePage(0);
  setIntroImageHasMore(true);
  setIntroImageLoading(false);

  const existingModal = document.getElementById('introImageSelectModal');
  if (existingModal) existingModal.remove();

  const modal = document.createElement('div');
  modal.id = 'introImageSelectModal';
  modal.className = 'bottom-sheet-overlay';
  modal.innerHTML = `
    <div class="bottom-sheet-backdrop" data-action="closeModal"></div>
    <div class="bottom-sheet intro-image-bottom-sheet" style="max-height: 80vh;">
      <div class="bottom-sheet-handle"></div>
      <div class="bottom-sheet-header">
        <h3>${title}</h3>
      </div>
      <div class="bottom-sheet-body intro-image-select-body" id="introImageSelectBody">
        <div class="intro-image-grid" id="introImageGrid"></div>
      </div>
    </div>
  `;

  // 이벤트 위임 설정
  modal.addEventListener('click', (e) => {
    const actionEl = e.target.closest('[data-action]');
    if (!actionEl) return;

    const action = actionEl.dataset.action;
    if (action === 'closeModal') {
      closeIntroImageModal();
    } else if (action === 'selectIntroImage') {
      const url = actionEl.dataset.url;
      if (getIntroImageMode() === 'insert') {
        insertIntroImage(url);
      } else {
        replaceIntroImage(url);
      }
    }
  });

  document.body.appendChild(modal);

  requestAnimationFrame(() => {
    modal.classList.add('open');
  });

  renderIntroImageGrid();

  const body = document.getElementById('introImageSelectBody');
  if (body) {
    body.addEventListener('scroll', handleIntroImageScroll);
  }
}

function renderIntroImageGrid(append = false) {
  const grid = document.getElementById('introImageGrid');
  if (!grid) return;

  const currentEditorData = getCurrentEditorData();
  const characterImages = currentEditorData.images || [];
  const generatedImages = currentEditorData.generatedImages || [];

  const allImages = [
    ...characterImages.map(img => ({ url: img.url, type: 'character' })),
    ...generatedImages.map(img => ({ url: img.url, type: 'generated' }))
  ];

  const start = getIntroImagePage() * INTRO_IMAGE_PAGE_SIZE;
  const end = start + INTRO_IMAGE_PAGE_SIZE;
  const pageImages = allImages.slice(start, end);

  setIntroImageHasMore(end < allImages.length);

  const html = pageImages.map(img => `
    <div class="intro-image-item" data-action="selectIntroImage" data-url="${img.url}">
      <img src="${img.url}" alt="이미지" loading="lazy">
    </div>
  `).join('');

  if (append) {
    grid.insertAdjacentHTML('beforeend', html);
  } else {
    grid.innerHTML = html || '<div class="intro-image-empty">이미지가 없습니다</div>';
  }

  setIntroImageLoading(false);
}

function handleIntroImageScroll(e) {
  const { scrollTop, scrollHeight, clientHeight } = e.target;

  if (scrollTop + clientHeight >= scrollHeight - 100 && !getIntroImageLoading() && getIntroImageHasMore()) {
    setIntroImageLoading(true);
    setIntroImagePage(getIntroImagePage() + 1);
    renderIntroImageGrid(true);
  }
}

export function insertIntroImage(imageUrl) {
  getIntroBlocks().push({
    type: 'image',
    content: imageUrl
  });

  closeIntroImageModal();
  renderIntroPreview();
  updatePublishButtonState();
  updateTabCheckStatus();

  const container = document.getElementById('introChatPreview');
  if (container) {
    container.scrollTop = container.scrollHeight;
  }

  showToast('이미지가 추가되었습니다');
}

export function replaceIntroImage(imageUrl) {
  const editingIndex = getEditingIntroImageIndex();
  const introBlocks = getIntroBlocks();
  if (editingIndex !== null && editingIndex >= 0 && editingIndex < introBlocks.length) {
    introBlocks[editingIndex].content = imageUrl;
    closeIntroImageModal();
    renderIntroPreview();
    updatePublishButtonState();
    updateTabCheckStatus();
    showToast('이미지가 변경되었습니다');
  }
  setEditingIntroImageIndex(null);
}

export function closeIntroImageModal() {
  const modal = document.getElementById('introImageSelectModal');
  if (modal) {
    // 스크롤 리스너 정리 (메모리 누수 방지)
    const body = document.getElementById('introImageSelectBody');
    if (body) {
      body.removeEventListener('scroll', handleIntroImageScroll);
    }
    modal.classList.remove('open');
    setTimeout(() => modal.remove(), 300);
  }
  if (getIntroImageMode() === 'replace') {
    setEditingIntroImageIndex(null);
  }
}

// ============================================
// 블록을 firstMessage 형식으로 변환
// ============================================

export function blocksToFirstMessage() {
  const introImages = [];
  let imageIndex = 0;

  const firstMessage = getIntroBlocks().map(block => {
    let marker;
    if (block.type === 'narration') marker = 'N';
    else if (block.type === 'dialogue') marker = 'C';
    else if (block.type === 'thought') marker = 'T';
    else if (block.type === 'user') marker = 'U';
    else if (block.type === 'image') {
      marker = 'I';
      introImages.push(block.content);
      return `{${marker}}${imageIndex++}`;
    }
    else marker = 'N';
    return `{${marker}}${block.content}`;
  }).join('\n');

  return { firstMessage, introImages };
}
